/**
 * Servicio de Límites Municipales
 * 
 * Carga y consulta límites municipales desde el snapshot R2 (DERA G13)
 * servido por el Worker geodata (`boundaries/muni/{INE}.json`), bajo demanda.
 * Permite validar coordenadas contra polígonos oficiales.
 * 
 * @module services/boundaries/MunicipalBoundaryService
 */

import type { MunicipalBoundary } from '../../lib/spatialValidation';
import type { Polygon, MultiPolygon, Feature } from 'geojson';
import bbox from '@turf/bbox';
import proj4 from 'proj4';
import { fetchWithBackoff } from '../../utils/fetchWithBackoff';

// Registrar definiciones proj4 si no están
if (!proj4.defs('EPSG:25830')) {
  proj4.defs('EPSG:25830', '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs');
}
if (!proj4.defs('EPSG:4326')) {
  proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');
}

// ============================================================================
// TIPOS BBOX
// ============================================================================

/**
 * Bounding Box [minX, minY, maxX, maxY] en EPSG:4326
 * Compatible con WFS CQL_FILTER BBOX
 */
export type BBox = [number, number, number, number];

// ============================================================================
// TIPOS
// ============================================================================

export interface BoundaryServiceConfig {
  /** URL base del Worker geodata (default: producción R2) */
  baseUrl?: string;
  /** Timeout por intento en ms (default: 10000) */
  timeout?: number;
}

/** Propiedades del snapshot R2 `boundaries/muni/{INE}.json` (esquema 1.0.0). */
interface R2BoundaryProperties {
  codigoINE: string;
  nombre: string;
  provincia?: string;
  id_dera?: number;
}

// ============================================================================
// CACHE EN MEMORIA
// ============================================================================

const boundaryCache = new Map<string, MunicipalBoundary>();
/** Municipios cuya carga R2 falló (404 / red). Contador visible — no se enmascara. */
let boundaryMissCount = 0;

/**
 * VALMUNI (DOP-1 b): estado de carga POR MUNICIPIO. Sustituye al antiguo
 * indicador global `isLoaded`, que pasaba a `true` con el primer municipio y
 * bloqueaba la carga de todos los demás.
 *   - 'sin_limite': R2 respondió sin snapshot (404 o cuerpo inválido).
 *   - 'error_red':  red o *timeout* agotados.
 * El fallo se recuerda para la sesión: no se repite la petición en cada fila.
 */
export type BoundaryMissReason = 'sin_limite' | 'error_red';
export type BoundaryLoadStatus = 'cargado' | BoundaryMissReason | 'no_pedido';
const boundaryMisses = new Map<string, BoundaryMissReason>();
/** Peticiones en curso: dos filas concurrentes del mismo municipio comparten una sola. */
const inFlight = new Map<string, Promise<MunicipalBoundary | null>>();

// ============================================================================
// CONFIGURACIÓN
// ============================================================================

const DEFAULT_BASE_URL = 'https://geodata.pteltool.es';

const DEFAULT_CONFIG: Required<BoundaryServiceConfig> = {
  baseUrl: DEFAULT_BASE_URL,
  timeout: 10000
};

// ============================================================================
// FUNCIONES PÚBLICAS
// ============================================================================

/**
 * Carga el límite municipal de un municipio desde R2, bajo demanda.
 *
 * Consume `{baseUrl}/boundaries/muni/{INE}.json` (snapshot DERA G13, EPSG:4326)
 * y lo cachea en memoria. Idempotente: si ya está en cache, lo devuelve sin red.
 *
 * Degradación grácil (DOP-S3-FALLBACK): ante 404 (INE catastral o sin snapshot,
 * BL-04) o fallo de red agotado → devuelve `null` e incrementa el contador
 * visible `getBoundaryMissCount()`. No enmascara el fallo (HX-FALLBACK-ENMASCARA-BUG).
 *
 * @param codigoINE - Código INE 5 dígitos
 * @param config - Configuración opcional (baseUrl, timeout)
 * @returns MunicipalBoundary (geometría en EPSG:4326) o null si no disponible
 */
export async function loadBoundaryByINE(
  codigoINE: string,
  config: BoundaryServiceConfig = {}
): Promise<MunicipalBoundary | null> {
  const cached = boundaryCache.get(codigoINE);
  if (cached) return cached;
  // VALMUNI: un fallo ya registrado no se vuelve a pedir en la sesión.
  if (boundaryMisses.has(codigoINE)) return null;
  const pending = inFlight.get(codigoINE);
  if (pending) return pending;

  const promise = fetchBoundary(codigoINE, config).finally(() => inFlight.delete(codigoINE));
  inFlight.set(codigoINE, promise);
  return promise;
}

function registerMiss(codigoINE: string, reason: BoundaryMissReason): null {
  boundaryMisses.set(codigoINE, reason);
  boundaryMissCount++;
  return null;
}

async function fetchBoundary(
  codigoINE: string,
  config: BoundaryServiceConfig
): Promise<MunicipalBoundary | null> {
  const { baseUrl, timeout } = { ...DEFAULT_CONFIG, ...config };
  const url = `${baseUrl}/boundaries/muni/${codigoINE}.json`;

  try {
    const { response } = await fetchWithBackoff(url, { timeoutMs: timeout });

    // 404 = ausencia legítima; fetchWithBackoff no reintenta 4xx → devuelve la respuesta.
    if (!response.ok) {
      return registerMiss(codigoINE, response.status >= 500 ? 'error_red' : 'sin_limite');
    }

    const feature = await response.json() as Feature<
      Polygon | MultiPolygon,
      R2BoundaryProperties
    >;
    const props = feature?.properties;
    const geometry = feature?.geometry;

    if (!props?.codigoINE || !geometry) {
      return registerMiss(codigoINE, 'sin_limite');
    }

    // DOP-S3-PROJECTION=β: se cachea la geometría en EPSG:4326 tal cual llega,
    // homogéneo con el scoring (booleanPointInPolygon opera en 4326). La
    // reproyección a 25830 se hace en el punto de uso (getBBoxByINEinUTM30).
    const boundary: MunicipalBoundary = {
      codigoINE: props.codigoINE,
      nombre: props.nombre ?? '',
      geometry
    };

    boundaryCache.set(props.codigoINE, boundary);
    // Se indexa también por el INE pedido, por si el snapshot trae otro código.
    boundaryCache.set(codigoINE, boundary);
    return boundary;
  } catch {
    // Timeout interno o red agotada tras reintentos → degradación grácil.
    return registerMiss(codigoINE, 'error_red');
  }
}

/**
 * VALMUNI: estado de carga del límite de UN municipio.
 * 'no_pedido' = ni cargado ni fallido (no se ha intentado en esta sesión).
 */
export function getBoundaryLoadStatus(codigoINE: string): BoundaryLoadStatus {
  if (boundaryCache.has(codigoINE)) return 'cargado';
  return boundaryMisses.get(codigoINE) ?? 'no_pedido';
}

/** VALMUNI-b (DOP-1 a): espera máxima de la precarga antes de la primera fila. */
export const PRECARGA_CONTORNO_TOPE_MS = 3000;

/**
 * VALMUNI-b (DOP-1 a): precarga el contorno del municipio del documento y lo
 * espera como mucho `topeMs`. Antes, las primeras filas de cada documento se
 * geolocalizaban sin contorno (125 de 1 272 en el corpus): TRF-06 y el WFS de
 * energía trabajaban sin zona.
 *
 * El tope no cancela la petición ni la da por fallida: si llega tarde, llena la
 * caché igual y la usan las filas siguientes. No lanza. Devuelve el estado del
 * contorno al acabar la espera.
 */
export async function precargarContorno(
  codigoINE: string | null | undefined,
  topeMs: number = PRECARGA_CONTORNO_TOPE_MS
): Promise<BoundaryLoadStatus> {
  if (!codigoINE || !/^\d{5}$/.test(codigoINE) || codigoINE === '00000') return 'no_pedido';
  let espera: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    loadBoundaryByINE(codigoINE),
    new Promise<void>(resolve => { espera = setTimeout(resolve, topeMs); }),
  ]);
  clearTimeout(espera);
  return getBoundaryLoadStatus(codigoINE);
}

/**
 * VALMUNI-b2 (DOP-2 a): motivo de cada fallo en el aviso de documento. Texto
 * sellado por Luis el 31-oct-2026; P2, P4 y el informe dicen exactamente esto.
 */
const MOTIVO_AVISO_SIN_VALIDAR: Record<BoundaryMissReason, string> = {
  sin_limite: 'no hay límite publicado para este código INE',
  error_red: 'fallo de red al cargarlo',
};

/**
 * VALMUNI-b2 (DOP-2 a): aviso de documento «sin validar contra el término
 * municipal». Solo con los dos motivos sellados; con el límite cargado o sin
 * pedir (`no_pedido`) devuelve `null` y no hay aviso. No bloquea ni baja la nota.
 */
export function textoSinValidarContorno(
  municipio: string,
  estado: BoundaryLoadStatus
): string | null {
  if (estado !== 'sin_limite' && estado !== 'error_red') return null;
  return `Sin validar contra el término municipal: no se pudo cargar el límite de ${municipio} (${MOTIVO_AVISO_SIN_VALIDAR[estado]}). Las coordenadas no se han comprobado dentro del término.`;
}

/**
 * VALMUNI-b2a (DOP-2, ajuste «P2 escucha»): estado del límite SIN lanzar
 * petición nueva. Si hay una en curso (precarga de P1 o geolocalización),
 * espera su respuesta, sin tope; si no, lee el estado tal cual. No lanza.
 */
export async function escucharEstadoContorno(
  codigoINE: string | null | undefined
): Promise<BoundaryLoadStatus> {
  if (!codigoINE) return 'no_pedido';
  const pendiente = inFlight.get(codigoINE);
  if (pendiente) await pendiente;
  return getBoundaryLoadStatus(codigoINE);
}

/**
 * Número de municipios cuya carga R2 falló (404 / red). Contador visible para
 * exponer la degradación grácil sin enmascararla (DOP-S3-FALLBACK).
 */
export function getBoundaryMissCount(): number {
  return boundaryMissCount;
}

/**
 * Obtiene límite municipal por código INE
 * 
 * @param codigoINE - Código INE 5 dígitos
 * @returns MunicipalBoundary o null si no existe
 */
export function getBoundaryByINE(codigoINE: string): MunicipalBoundary | null {
  return boundaryCache.get(codigoINE) ?? null;
}

/**
 * Obtiene límite municipal por nombre (búsqueda exacta insensible a mayúsculas)
 * 
 * @param nombre - Nombre del municipio
 * @returns MunicipalBoundary o null si no existe
 */
export function getBoundaryByName(nombre: string): MunicipalBoundary | null {
  const normalizado = nombre.toLowerCase().trim();
  
  for (const boundary of boundaryCache.values()) {
    if (boundary.nombre.toLowerCase() === normalizado) {
      return boundary;
    }
  }
  
  return null;
}

/**
 * ¿Hay algún límite en caché?
 *
 * @deprecated VALMUNI: no sirve para saber si UN municipio está cargado; usar
 * `getBoundaryLoadStatus(ine)` o `getBoundaryByINE(ine)`. Antes era un indicador
 * global que impedía cargar cualquier municipio que no fuera el primero.
 */
export function isBoundariesLoaded(): boolean {
  return boundaryCache.size > 0;
}

/**
 * Obtiene número de municipios en cache
 */
export function getBoundaryCount(): number {
  return boundaryCache.size;
}

/**
 * Limpia la cache (útil para tests)
 */
export function clearBoundaryCache(): void {
  boundaryCache.clear();
  boundaryMisses.clear();
  inFlight.clear();
  boundaryMissCount = 0;
}

/**
 * Obtiene Bounding Box de un municipio por código INE
 * 
 * @param codigoINE - Código INE 5 dígitos
 * @returns BBox [minX, minY, maxX, maxY] en EPSG:4326 o null si no existe
 * 
 * @example
 * const bbox = getBBoxByINE('18051'); // Colomera
 * // Returns: [-3.79, 37.32, -3.55, 37.52] aprox
 */
export function getBBoxByINE(codigoINE: string): BBox | null {
  const boundary = boundaryCache.get(codigoINE);
  if (!boundary) return null;
  
  const feature: Feature<Polygon | MultiPolygon> = {
    type: 'Feature',
    properties: {},
    geometry: boundary.geometry
  };
  
  const result = bbox(feature);
  return result as BBox;
}

/**
 * Obtiene Bounding Box de un municipio por nombre
 * 
 * @param nombre - Nombre del municipio (búsqueda insensible a mayúsculas)
 * @returns BBox [minX, minY, maxX, maxY] en EPSG:4326 o null si no existe
 * 
 * @example
 * const bbox = getBBoxByName('Colomera');
 * // Returns: [-3.79, 37.32, -3.55, 37.52] aprox
 */
export function getBBoxByName(nombre: string): BBox | null {
  const boundary = getBoundaryByName(nombre);
  if (!boundary) return null;
  
  const feature: Feature<Polygon | MultiPolygon> = {
    type: 'Feature',
    properties: {},
    geometry: boundary.geometry
  };
  
  const result = bbox(feature);
  return result as BBox;
}

/**
 * Reproyecta un BBox de WGS84 (EPSG:4326) a UTM30 (EPSG:25830)
 * 
 * @param bboxWGS84 - BBox en [minX, minY, maxX, maxY] WGS84
 * @returns BBox en UTM30 ETRS89 o null si falla
 */
export function reprojectBBoxToUTM30(bboxWGS84: BBox): BBox | null {
  try {
    const [minLng, minLat, maxLng, maxLat] = bboxWGS84;
    
    // Reproyectar esquinas
    const swUTM = proj4('EPSG:4326', 'EPSG:25830', [minLng, minLat]);
    const neUTM = proj4('EPSG:4326', 'EPSG:25830', [maxLng, maxLat]);
    
    return [swUTM[0], swUTM[1], neUTM[0], neUTM[1]];
  } catch (error) {
    console.error('Error reproyectando BBOX:', error);
    return null;
  }
}

/**
 * Obtiene BBox de un municipio en UTM30 (EPSG:25830) por código INE
 * 
 * Compatible con WFS CQL_FILTER BBOX que esperan coordenadas UTM
 * 
 * @param codigoINE - Código INE 5 dígitos
 * @returns BBox [minX, minY, maxX, maxY] en EPSG:25830 o null
 * 
 * @example
 * const bbox = getBBoxByINEinUTM30('18051'); // Colomera
 * // Returns: [430000, 4140000, 450000, 4160000] aprox (UTM30)
 */
export function getBBoxByINEinUTM30(codigoINE: string): BBox | null {
  const bboxWGS84 = getBBoxByINE(codigoINE);
  if (!bboxWGS84) return null;
  
  return reprojectBBoxToUTM30(bboxWGS84);
}

/**
 * Obtiene BBox de un municipio en UTM30 (EPSG:25830) por nombre
 * 
 * @param nombre - Nombre del municipio
 * @returns BBox [minX, minY, maxX, maxY] en EPSG:25830 o null
 */
export function getBBoxByNameInUTM30(nombre: string): BBox | null {
  const bboxWGS84 = getBBoxByName(nombre);
  if (!bboxWGS84) return null;
  
  return reprojectBBoxToUTM30(bboxWGS84);
}

// ============================================================================
// CROSS-VALIDATION ESPACIAL (F2.T3)
// ============================================================================

/**
 * Resultado de validación espacial de un punto
 */
export interface SpatialValidationResult {
  /** ¿El punto está dentro del BBOX? */
  isValid: boolean;
  /** Código INE del municipio validado */
  municipalityCode: string;
  /** Nombre del municipio */
  municipalityName: string;
  /** Distancia al centro del BBOX en metros (solo si isValid=false) */
  distanceToCenter?: number;
  /** BBOX usado para validación [minX, minY, maxX, maxY] */
  bbox: BBox;
  /** Sistema de coordenadas del punto de entrada */
  inputCRS: 'EPSG:4326' | 'EPSG:25830';
}

/**
 * Verifica si un punto está dentro del BBOX de un municipio
 * 
 * @param x - Coordenada X (longitud en WGS84 o Este en UTM30)
 * @param y - Coordenada Y (latitud en WGS84 o Norte en UTM30)
 * @param codigoINE - Código INE del municipio (5 dígitos)
 * @param inputCRS - Sistema de coordenadas de entrada (default: EPSG:25830)
 * @returns Resultado de validación o null si el municipio no existe
 * 
 * @example
 * // Validar punto UTM30 en Colomera
 * const result = validatePointInMunicipality(437000, 4147000, '18051');
 * if (result?.isValid) {
 *   console.log('Punto dentro de Colomera');
 * }
 */
export function validatePointInMunicipality(
  x: number,
  y: number,
  codigoINE: string,
  inputCRS: 'EPSG:4326' | 'EPSG:25830' = 'EPSG:25830'
): SpatialValidationResult | null {
  const boundary = boundaryCache.get(codigoINE);
  if (!boundary) return null;

  // Obtener BBOX en el mismo CRS que el punto de entrada
  let bbox: BBox | null;
  if (inputCRS === 'EPSG:25830') {
    bbox = getBBoxByINEinUTM30(codigoINE);
  } else {
    bbox = getBBoxByINE(codigoINE);
  }

  if (!bbox) return null;

  const [minX, minY, maxX, maxY] = bbox;
  const isValid = x >= minX && x <= maxX && y >= minY && y <= maxY;

  const result: SpatialValidationResult = {
    isValid,
    municipalityCode: codigoINE,
    municipalityName: boundary.nombre,
    bbox,
    inputCRS
  };

  // Si no es válido, calcular distancia al centro del BBOX
  if (!isValid) {
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    
    if (inputCRS === 'EPSG:25830') {
      // Distancia euclidiana directa en metros (UTM)
      result.distanceToCenter = Math.sqrt(
        Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2)
      );
    } else {
      // Distancia aproximada Haversine para WGS84
      result.distanceToCenter = haversineDistance(y, x, centerY, centerX);
    }
  }

  return result;
}

/**
 * Valida múltiples puntos contra el BBOX de un municipio
 * 
 * @param points - Array de puntos [x, y] a validar
 * @param codigoINE - Código INE del municipio
 * @param inputCRS - Sistema de coordenadas (default: EPSG:25830)
 * @returns Array de resultados de validación
 */
export function validatePointsInMunicipality(
  points: Array<[number, number]>,
  codigoINE: string,
  inputCRS: 'EPSG:4326' | 'EPSG:25830' = 'EPSG:25830'
): SpatialValidationResult[] {
  return points
    .map(([x, y]) => validatePointInMunicipality(x, y, codigoINE, inputCRS))
    .filter((result): result is SpatialValidationResult => result !== null);
}

/**
 * Encuentra el municipio más cercano a un punto dado
 * 
 * Útil para sugerir correcciones cuando un punto cae fuera del BBOX esperado.
 * Busca entre todos los municipios cargados en cache.
 * 
 * @param x - Coordenada X
 * @param y - Coordenada Y  
 * @param inputCRS - Sistema de coordenadas (default: EPSG:25830)
 * @param maxResults - Número máximo de resultados (default: 3)
 * @returns Array de municipios ordenados por distancia al centro
 */
export function findNearestMunicipalities(
  x: number,
  y: number,
  inputCRS: 'EPSG:4326' | 'EPSG:25830' = 'EPSG:25830',
  maxResults: number = 3
): Array<{ codigoINE: string; nombre: string; distance: number; isInside: boolean }> {
  const results: Array<{ codigoINE: string; nombre: string; distance: number; isInside: boolean }> = [];

  for (const [codigoINE, boundary] of boundaryCache) {
    const validation = validatePointInMunicipality(x, y, codigoINE, inputCRS);
    if (!validation) continue;

    // Calcular distancia al centro aunque esté dentro
    const [minX, minY, maxX, maxY] = validation.bbox;
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    let distance: number;
    if (inputCRS === 'EPSG:25830') {
      distance = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
    } else {
      distance = haversineDistance(y, x, centerY, centerX);
    }

    results.push({
      codigoINE,
      nombre: boundary.nombre,
      distance,
      isInside: validation.isValid
    });
  }

  // Ordenar por distancia y retornar top N
  return results
    .sort((a, b) => a.distance - b.distance)
    .slice(0, maxResults);
}

/**
 * Calcula distancia Haversine entre dos puntos en WGS84
 * 
 * @param lat1 - Latitud punto 1
 * @param lon1 - Longitud punto 1
 * @param lat2 - Latitud punto 2
 * @param lon2 - Longitud punto 2
 * @returns Distancia en metros
 */
function haversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371000; // Radio de la Tierra en metros
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Convierte grados a radianes
 */
function toRad(degrees: number): number {
  return degrees * (Math.PI / 180);
}
